// Copy + asset config for the Starter-plan Elements engagement / upsell page (PE-1624).
//
// The /elements surface is a single page (unlike Marketing's 5 surfaces), so this is
// one flat config, not a per-surface record — adding a surface key that would only ever
// have one entry would be needless indirection (see the dashboard CLAUDE.md design rules
// on connascence / over-abstraction).
//
// PURELY DATA. The rendering (icon mapping, layout, CTA wiring) lives in
// ElementsEngagement.tsx. The component reads `unlockTier` from the engine
// (unlockTierLabel(FEATURE.FUNDRAISING_BAR)) — no tier copy is hard-coded here.
//
// The hero previews are SELF-CONTAINED CSS/React mini-renders (see PreviewKind), not
// image files — they render representative stand-ins of each element (overlay popup,
// add-to-cart, supporter list, fundraising bar, QR code) built from the dashboard's
// design tokens. This keeps the page inside the dashboard's package boundary (no
// donation-elements import) and means it never 404s on a missing asset. Swap any one for
// a real product screenshot later by giving its PreviewShot a `src` instead of a `kind`.

// Icon shown in a booster card's tinted square. Closed union (not a free string) so a
// typo surfaces at compile time; mapped to a Heroicon component in ElementsEngagement.
export type BoosterIconKey =
  | 'bar'
  | 'users'
  | 'flag'
  | 'qr'
  | 'link'
  | 'overlay'
  | 'cart'
  | 'portal';

export interface BoosterItem {
  title: string;
  /** ≤10 words — scannable, not a paragraph. */
  description: string;
  icon: BoosterIconKey;
}

// Which self-contained mini-render a preview shot draws. Closed union (not a free string)
// so a typo surfaces at compile time; mapped to a render fn in ElementsEngagement.
export type PreviewKind = 'overlay' | 'add_to_cart' | 'supporters' | 'bar' | 'qr';

/** A labeled element preview in the hero. Renders `kind` (a CSS mini) unless `src` (a
 *  real screenshot under /assets/elements/) is set — `src` wins when present. */
export interface PreviewShot {
  /** Caption above the shot, e.g. "Supporters". */
  label: string;
  /** Which self-contained mini-render to draw. */
  kind: PreviewKind;
  /** Optional public-asset path; when set, an <img> is rendered instead of the mini. */
  src?: string;
}

export interface ElementsEngagementContent {
  /** Headline — value-led, not "what the tier is." */
  title: string;
  /** One-line, plain-language pitch. */
  pitch: string;
  /** The big overlay showcase — left column of the preview cluster, vertically centered,
   *  container fits the full image. */
  overlayShot: PreviewShot;
  /** The stacked previews in the right column (fundraising bar, supporters, add-to-cart),
   *  all rendered at the same width. */
  sideShots: PreviewShot[];
  /** Optional trailing "and more" text (stands in for the elements without a screenshot).
   *  Omit to show none. */
  andMore?: string;
  /** Every element as its own card, shown in a grid below the hero. */
  boosters: BoosterItem[];
}

export const ELEMENTS_ENGAGEMENT_CONTENT: ElementsEngagementContent = {
  title: 'Fundraising boosters that raise more',
  pitch:
    'Give supporters more ways to donate, and show the progress every gift is building. An easier and more educated visitor experience maximizes conversion and giving.',
  // Preview cluster: the overlay showcase fills the left column; the other three previews
  // stack in the right column at a shared width. `kind` remains the fallback render if a
  // `src` file is ever missing.
  overlayShot: {
    label: 'Website overlay',
    kind: 'overlay',
    src: '/assets/elements/overlay-preview.png',
  },
  sideShots: [
    { label: 'Fundraising bar', kind: 'bar', src: '/assets/elements/fundraising-bar-preview.png' },
    { label: 'Supporters', kind: 'supporters', src: '/assets/elements/supporters-preview.png' },
    { label: 'Add-to-Cart', kind: 'add_to_cart', src: '/assets/elements/add-to-cart-preview.png' },
  ],
  // Small "and more" text stands in for the elements without a screenshot (QR codes,
  // donation flags, prefilled links) so the cluster reads as "these + more".
  andMore: 'and more',
  // Every element is its own card (flat list, no groups) — styled like the
  // MarketingEngagement benefit cards.
  boosters: [
    {
      title: 'Portal Widget',
      description:
        'Give donors a portal to manage subscriptions, view payment history, and access tax documents — all from your site.',
      icon: 'portal',
    },
    {
      title: 'Fundraising Bar',
      description:
        'A live progress bar toward your fundraising goal. Shows momentum in real time — creating urgency that nudges hesitant visitors to donate.',
      icon: 'bar',
    },
    {
      title: 'Supporter Lists',
      description:
        'A live feed of recent donors and amounts. Show social proof and incentivize donors to also support the cause.',
      icon: 'users',
    },
    {
      title: 'Donation Flags',
      description:
        'Pin a simple donation flag anywhere on your own site, and display a form when clicked. Turn high-traffic pages into giving opportunities.',
      icon: 'flag',
    },
    {
      title: 'QR Codes',
      description:
        'A scannable code for flyers, screens, and events. Bridge in-person interactions to online donations.',
      icon: 'qr',
    },
    {
      title: 'Website Overlay',
      description:
        'Effortlessly add a form to cleanly overlay a page, allowing any link to be a surface for donations.',
      icon: 'overlay',
    },
    {
      title: 'Add-to-Cart',
      description:
        'Let donors give multiple gifts in one, streamlined checkout. Raise your average gift value.',
      icon: 'cart',
    },
  ],
};
